import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Pill } from 'ui-components/components/pill';

import { startImport } from '../../../../store/customers/import/customers.import.actions';
import messages from './messages';
import style from './import-customers-modal.module.less';

const ImportCustomersModal = ({ modalFrameworkMethods }) => {
  const { closeModal } = modalFrameworkMethods;
  const dispatch = useDispatch();

  const [file, setFile] = useState(null);
  const [fileExtensionError, setFileExtensionError] = useState(null);
  const [updateExistingRecord, setUpdateExistingRecord] = useState(true);
  const [ignoreCsvRecord, setIgnoreCsvRecord] = useState(false);
  const [displayUploadMessage, setDisplayUploadMessage] = useState(false);

  const onFileSelect = useCallback(() => {
    const importCustomers = document.getElementById('importCustomers');

    importCustomers.value = '';
    setFileExtensionError(null);
    importCustomers.click();
  }, [setFileExtensionError]);

  const handleFileUpload = useCallback(
    (event) => {
      const selectedFile = event.target.files[0];
      const allowedExtensions = ['csv'];
      if (selectedFile) {
        const uploadedFileExt = selectedFile.name.substr(selectedFile.name.lastIndexOf('.') + 1).toLowerCase();

        if (allowedExtensions.includes(uploadedFileExt)) {
          const fileUploadReader = new FileReader();
          fileUploadReader.onload = () => {
            setFile(selectedFile);
          };
          return fileUploadReader.readAsText(selectedFile);
        }
      }
      setFileExtensionError(<FormattedMessage {...messages.notCsvError} />);
    },
    [setFile, setFileExtensionError],
  );

  const handleUpdateExistingRecordChecked = useCallback(() => {
    setUpdateExistingRecord(true);
    setIgnoreCsvRecord(false);
  }, [setUpdateExistingRecord, setIgnoreCsvRecord]);

  const handleIgnoreCsvRecordChecked = useCallback(() => {
    setIgnoreCsvRecord(true);
    setUpdateExistingRecord(false);
  }, [setIgnoreCsvRecord, setUpdateExistingRecord]);

  const handleStartImport = useCallback(() => {
    dispatch(startImport(ignoreCsvRecord, file));
    setDisplayUploadMessage(true);
  }, [ignoreCsvRecord, file, dispatch, setDisplayUploadMessage]);

  return (
    <Modal className={style['import-customers-modal']} size='medium'>
      <ModalHeader className={style['import-customers-modal-header']} closeModal={closeModal}>
        <FormattedMessage {...messages.title} />
      </ModalHeader>
      <ModalBody className={style['import-customers-modal-body']}>
        {!displayUploadMessage && (
          <div className={style['import-customers-content']}>
            <div className={style['import-customers-summary']}>
              <p>
                <FormattedMessage {...messages.importCustomersSummary} />
              </p>
              <span className={style['customer-import-section-link']}>
                <a href='/s3/marketplaces/shipstation/CustomerImportSample.csv' id={style['customer-import-sample-CSV-file-link']}>
                  <FormattedMessage defaultMessage='Download a sample CSV File' description='messages.downloadSampleCsv' id='nzc8mT' />
                </a>
              </span>
            </div>
            <div className={style['select-file-to-import']}>
              <h2>
                <b>
                  <FormattedMessage {...messages.selectFile} />
                </b>
              </h2>
              <div className={style['customer-import-select-button']}>
                <Button onClick={onFileSelect} theme='tertiary'>
                  <FormattedMessage defaultMessage='Select a File' description='messages.selectAFile' id='71XpP7' />
                </Button>
                <input
                  className={style['customer-import-input']}
                  data-testid='import-customers-input'
                  id='importCustomers'
                  name='importCustomers'
                  onChange={handleFileUpload}
                  type='file'
                />
                {file && (
                  <div className={style['customer-import-pill']}>
                    <Pill label={file.name} onRemove={() => setFile(null)} theme='in-progress' />
                  </div>
                )}
                {fileExtensionError && <span className={style['customer-import-file-extension-error']}>{fileExtensionError}</span>}
              </div>
            </div>
            <div className={style['select-options']}>
              <h2>
                <FormattedMessage {...messages.selectOptions} />
              </h2>
              <p>
                <FormattedMessage {...messages.selectOptionsSummary} />
              </p>
              <div className={style['select-options-radio-group']}>
                <RadioCheckList>
                  <RadioCheckListItem
                    className={style['select-options-radio-button']}
                    isChecked={updateExistingRecord}
                    onChecked={handleUpdateExistingRecordChecked}
                  >
                    <FormattedMessage {...messages.updateSelectOption} />
                  </RadioCheckListItem>
                  <RadioCheckListItem
                    className={style['select-options-radio-button']}
                    isChecked={ignoreCsvRecord}
                    onChecked={handleIgnoreCsvRecordChecked}
                  >
                    <FormattedMessage {...messages.ignoreSelectOption} />
                  </RadioCheckListItem>
                </RadioCheckList>
              </div>
            </div>
          </div>
        )}
        {displayUploadMessage && (
          <div className={style['import-customers-content']}>
            <div className={style['import-customers-summary']}>
              <p>
                <FormattedMessage {...messages.importCustomersUploadMessage} />
              </p>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter className={style['import-customers-modal-footer']}>
        {!displayUploadMessage && (
          <>
            <Button onClick={closeModal} theme='tertiary'>
              <FormattedMessage {...messages.cancel} />
            </Button>
            <Button
              className={style['button-styles']}
              disabled={!((updateExistingRecord || ignoreCsvRecord) && file)}
              onClick={handleStartImport}
              theme='primary'
            >
              <FormattedMessage {...messages.startImport} />
            </Button>
          </>
        )}
        {displayUploadMessage && (
          <div className={style['import-customers-close-button']}>
            <Button onClick={closeModal} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.close} />
            </Button>
          </div>
        )}
      </ModalFooter>
    </Modal>
  );
};

ImportCustomersModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
};

export default ImportCustomersModal;
